import React, { useState, useEffect, useRef } from 'react';
import { Play, PlayCircle, List } from 'lucide-react';
import { newsletters, quickReads } from '../data/mock';

const VideoLibrary = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [showIndex, setShowIndex] = useState(false);
  const videoRefs = useRef({});

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Combine all videos from newsletters and quick reads (avoid duplicates by videoId)
  const allVideos = [];
  const seenVideoIds = new Set();

  // Add newsletter videos
  newsletters.forEach(item => {
    if (item.videoId && !seenVideoIds.has(item.videoId)) {
      seenVideoIds.add(item.videoId);
      allVideos.push({
        id: item.videoId,
        title: item.title,
        summary: item.summary,
        category: item.category,
        source: 'newsletter',
        sourceId: item.id
      });
    }
  });

  // Add quick read videos (only if not already added)
  quickReads.forEach(item => {
    if (item.videoId && !seenVideoIds.has(item.videoId)) {
      seenVideoIds.add(item.videoId);
      allVideos.push({
        id: item.videoId,
        title: item.title,
        summary: item.summary,
        category: item.category,
        source: 'quickread',
        sourceId: item.id
      });
    }
  });

  const scrollToVideo = (videoId) => {
    if (videoRefs.current[videoId]) {
      videoRefs.current[videoId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      setActiveVideo(videoId);
      setShowIndex(false);
    }
  };

  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 pattern-bg" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
              <Play className="h-6 w-6 text-primary" />
            </div>
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Educational Content</span>
          </div>
          
          <h1 className="font-display text-4xl lg:text-5xl xl:text-6xl text-foreground mb-6">
            Video Library
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground max-w-2xl">
            Watch our collection of educational videos covering everything you need to know about selling your business.
          </p>
        </div>
      </section>

      {/* Floating Index Button */}
      <button
        onClick={() => setShowIndex(!showIndex)}
        className="fixed bottom-6 right-6 z-40 btn-primary p-4 rounded-full shadow-lg hover:scale-105 transition-transform"
      >
        <List className="h-6 w-6" />
      </button>

      {/* Video Index Sidebar */}
      {showIndex && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowIndex(false)}
          />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-card border-l border-border shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6">
              <h2 className="font-display text-xl text-foreground">Video Index</h2>
              <p className="text-sm text-muted-foreground mt-1">Click to jump to a video</p>
            </div>
            <div className="p-4">
              {allVideos.map((video, index) => (
                <button
                  key={video.id}
                  onClick={() => scrollToVideo(video.id)}
                  className={`w-full text-left p-4 rounded-xl mb-2 transition-smooth ${
                    activeVideo === video.id 
                      ? 'bg-primary/10 border border-primary/30' 
                      : 'bg-secondary hover:bg-accent'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-sm font-semibold text-primary">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm line-clamp-2">{video.title}</p>
                      <span className="text-xs text-muted-foreground mt-1 block">{video.category}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Videos Grid */}
      <section className="bg-background relative">
        <div className="absolute inset-0 pattern-bg opacity-30" />
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-10 lg:py-14">
          <div className="space-y-12">
            {allVideos.map((video, index) => (
              <div 
                key={video.id}
                ref={el => videoRefs.current[video.id] = el}
                className={`scroll-mt-32 ${activeVideo === video.id ? 'ring-2 ring-primary ring-offset-4 ring-offset-background rounded-2xl' : ''}`}
              >
                {/* Video Header */}
                <div className="flex items-start gap-4 mb-4">
                  <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center text-lg font-semibold text-primary">
                    {index + 1}
                  </span>
                  <div>
                    <span className="category-badge mb-2 inline-block">{video.category}</span>
                    <h2 className="font-display text-xl lg:text-2xl text-foreground">
                      {video.title}
                    </h2>
                    <p className="text-muted-foreground mt-2 text-sm lg:text-base">
                      {video.summary}
                    </p>
                  </div>
                </div>

                {/* Video Player */}
                <div className="video-container border border-border rounded-2xl overflow-hidden">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?rel=0&modestbranding=1`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute top-0 left-0 w-full h-full"
                  />
                </div>
              </div>
            ))}
          </div>

          {allVideos.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-6">
                <PlayCircle className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-lg">
                No videos available yet.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default VideoLibrary;
